import {Injectable} from '@angular/core';
import {SynopticFile} from '../../model/synoptics/synoptic-file';
import {SynopticsPropertiesErrors} from './synoptics-properties-errors';
import {TranslocoService} from '@ngneat/transloco';
import {SynopticPersistRequestCsv} from '../../model/import-csv/synoptic-persist-request-csv';
import {CsvError, CsvErrorList} from '../../model/import-csv/csv-error';
import {EncodingUtils} from '../../shared/encoding-utils';
import {
  isSynopticElementStaticCsvConfiguration
} from '../../model/synoptics/types-guard/isSynopticElementStaticCsvConfiguration';
import {CsvConfigJsonResult, OperationTypes} from '../../model/synoptics/synoptic-configuration-csv-types';
import {ShowPopupArgsCsv} from '../../model/synoptics/synoptic-arg-types';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {
  isSynopticElementStateCsvConfiguration
} from '../../model/synoptics/types-guard/isSynopticElementStateCsvConfiguration';
import {isStyleByThresholdsArgs} from '../../model/synoptics/types-guard/isStyleByThresholdsArgs';
import {isReplaceTextValueArgs} from '../../model/synoptics/types-guard/isReplaceTextValueArgs';
import {isStyleByContinuousValueArgs} from '../../model/synoptics/types-guard/isStyleByContinousValueArgs';
import {isShowPopupArgs} from '../../model/synoptics/types-guard/isShowPopupArgs';
import {isExecuteCommandArgs} from '../../model/synoptics/types-guard/isExecuteCommandArgs';
import {isShowTooltipArgs} from '../../model/synoptics/types-guard/isShowTooltipArgs';
import {isLinkToSynopticArgs} from '../../model/synoptics/types-guard/isLinkToSynopticArgs';
import {
  isSynopticElementEventCsvConfiguration
} from '../../model/synoptics/types-guard/isSynopticElementEventCsvConfiguration';
import {isClosePopupArgs} from '../../model/synoptics/types-guard/isClosePopupArgs';

@Injectable({
  providedIn: 'root'
})
export class CsvSynopticsValidationService {
  private svgMap: Map<string, Document> = new Map<string, Document>();
  private synopticsPropertiesErrors: SynopticsPropertiesErrors = new SynopticsPropertiesErrors(this.i18n);

  constructor(private readonly i18n: TranslocoService, private readonly router: Router) {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      if (event.url === '/synoptic/new' || event.url === 'synoptic/edit') {
        this.removeAllFiles();
      }
    });
  }

  validate(svgFiles: SynopticFile[], synopticData: SynopticPersistRequestCsv): CsvErrorList {
    let errors: CsvErrorList = {};
    this.validateSvgAndId(svgFiles, synopticData.svg, synopticData.id, errors);
    try {
      const jsonParsed: Object = synopticData.configuration;
      this.validateJson(synopticData.operation, jsonParsed, errors, svgFiles);
    } catch (error) {
      this.addCsvMeasurementError(errors, 'configuration', {
        message: 'noValidJson',
        params: {
          field: this.synopticsPropertiesErrors?.getTranslation('configuration')?.toLowerCase(),
          value: synopticData.configuration,
        }
      });
    }
    return errors;
  }

  private addCsvMeasurementError(errorList: CsvErrorList, property: any, csvError: CsvError): void {
    if (!errorList.hasOwnProperty(property)) {
      errorList[property] = [];
    }
    errorList[property].push(csvError);
  }

  private validateSvgAndId(svgFiles: SynopticFile[], name: string, id: string, errorList: CsvErrorList): boolean {
    const file: SynopticFile = this.hasSvg(svgFiles, name, errorList);
    if (file) {
      this.hasElementInSvg(file, id, errorList);
    }
    return false;
  }

  private hasSvg(svgFiles: SynopticFile[], name: string, errorList: CsvErrorList, property: string = 'svg'): SynopticFile {
    const file: SynopticFile = svgFiles.find(file => file.name === name);
    if (!file) {
      this.addCsvMeasurementError(errorList, property, {
        message: 'fileNotFound',
        params: {
          field: this.synopticsPropertiesErrors.getTranslation(property).toLowerCase(),
          value: name,
        }
      });
      return undefined;
    }
    return file;
  }

  private hasElementInSvg(file: SynopticFile, elementId: string, errorList: CsvErrorList): boolean {
    let svg: Document;
    if (this.svgMap.has(file.name)) {
      svg = this.svgMap.get(file.name);
    } else {
      const parser: DOMParser = new DOMParser();
      svg = parser.parseFromString(EncodingUtils.base64DecodeUnicode(file.data), 'image/svg+xml');
      this.svgMap.set(file.name, svg);
    }

    const element: HTMLElement = svg.getElementById(elementId);
    if (!element) {
      this.addCsvMeasurementError(errorList, 'id', {
        message: 'idNotFound',
        params: {
          field: this.synopticsPropertiesErrors?.getTranslation('id')?.toLowerCase(),
          value: elementId,
        }
      });
      return false;
    }
    return true;
  }

  private validateJson(operation: OperationTypes, jsonData: any, errorList: CsvErrorList, svgFiles: SynopticFile[]): void {
    switch (operation) {
      case 'replaceTextValue':
        this.checkType(isSynopticElementStateCsvConfiguration, isReplaceTextValueArgs, jsonData, errorList);
        break;
      case 'styleByThresholds':
        this.checkType(isSynopticElementStateCsvConfiguration, isStyleByThresholdsArgs, jsonData, errorList);
        break;
      case 'styleByContinuousValue':
        this.checkType(isSynopticElementStateCsvConfiguration, isStyleByContinuousValueArgs, jsonData, errorList);
        break;
      case 'showPopup':
        this.checkType(isSynopticElementEventCsvConfiguration, isShowPopupArgs, jsonData, errorList, 'click');
        if (errorList['configuration'] === undefined) {
          const data: ShowPopupArgsCsv = jsonData.args;
          this.hasSvg(svgFiles, data.file, errorList, 'configuration');
        }
        break;
      case 'executeCommand':
        this.checkType(isSynopticElementEventCsvConfiguration, isExecuteCommandArgs, jsonData, errorList, 'click');
        break;
      case 'closePopup':
        this.checkType(isSynopticElementEventCsvConfiguration, isClosePopupArgs, jsonData, errorList, 'click');
        break;
      case 'showTooltip':
        this.checkType(isSynopticElementEventCsvConfiguration, isShowTooltipArgs, jsonData, errorList, 'hover');
        break;
      case 'linkToSynoptic':
        this.checkType(isSynopticElementEventCsvConfiguration, isLinkToSynopticArgs, jsonData, errorList, 'click');
        break;
      case 'text':
        this.checkType(isSynopticElementStaticCsvConfiguration, undefined, jsonData, errorList);
        break;
      default:
        this.addCsvMeasurementError(errorList, 'operation', {
          message: 'operation',
          params: {
            field: this.synopticsPropertiesErrors?.getTranslation('operation')?.toLowerCase(),
            value: operation,
          }
        });
    }
  }

  private checkType(validationFn: (data: any, fn?: (jsonData: any) => CsvConfigJsonResult, trigger?: 'click' | 'hover' | undefined) => CsvConfigJsonResult, specificValidationFn: (data: any) => CsvConfigJsonResult, jsonData: any, errorList: CsvErrorList, trigger?: 'click' | 'hover' | undefined): void {
    const result: CsvConfigJsonResult = validationFn(jsonData, specificValidationFn, trigger);
    if (!result.result) {
      this.addCsvMeasurementError(errorList, 'configuration', {
        message: 'noValidJson',
        params: {
          field: this.synopticsPropertiesErrors?.getTranslation('configuration')?.toLowerCase(),
          value: typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData),
          additionalInfo: result.errors
        }
      });
    }
  }

  public removeFileFromMap(fileName: string): void {
    this.svgMap.delete(fileName);
  }

  private removeAllFiles() {
    if (this.svgMap.size > 0) {
      this.svgMap = new Map<string, Document>();
    }
  }
}
